import { matchPath } from "react-router-dom";
import { createArrayFromObject } from "../Types/arrayUtilities";
import { isArray } from "../Types/typeUtilities";
import { allLocations, locationBehaviors, params } from "./locationConstants";
import { emptyGuid } from "../commonUtilities";

export interface LocationLink {
    label: string;
    to: string;
}

export interface AppLocation {
    getPath: (...args: string[]) => string;
    path: string;
    getParams: (currentPath: string) => Record<string, string> | null;
    label: string;
    link: LocationLink;
    getLink: (params?: (string | number | undefined)[] | string | number) => LocationLink;
    [key: string]: unknown;
}

const exception = (): string => {
    throw new Error("Method getUrl not implemented.");
};

export function createLocation(getPath: (...args: string[]) => string = exception, label: string, behaviors: string[] = []): AppLocation {
    const result: Record<string, unknown> = { getPath };
    result.path = getPath();
    result.getParams = (currentPath: string) => getParams(currentPath, result.path as string);
    result.label = label;
    result.link = createLink(label, result.path as string);
    result.getLink = (params?: (string | number | undefined)[] | string | number) => getLink(params, label, getPath);

    const allBehaviors = createArrayFromObject(locationBehaviors);
    for (let i = 0; i < allBehaviors.length; i++)
        result[allBehaviors[i] as string] = behaviors.includes(allBehaviors[i] as string);

    return result as unknown as AppLocation;
}

function getLink(linkParams: (string | number | undefined)[] | string | number | undefined, label: string, getPath: (...args: string[]) => string): LocationLink {
    if (isArray(linkParams))
        return createLink(label, getPath(...linkParams.map(p => String(p ?? emptyGuid))));
    return linkParams !== undefined
        ? createLink(label, getPath(String(linkParams)))
        : createLink(label, getPath());
}

function getParams(currentPath: string, routeToMatch: string): Record<string, string> | null {
    const matchedInfo = matchPath(routeToMatch, currentPath);

    return matchedInfo ? matchedInfo.params as Record<string, string> : null;
}

function createLink(label = "", path = ""): LocationLink {
    let to = path;

    for (let key in params) {
        const param = (params as Record<string, string>)[key];
        to = to.replace(param, emptyGuid);
    }

    return { label, to };
}

export function createEmptyLink(): LocationLink {
    return createLink();
}

export function getCurrentLocation(currentPath: string): AppLocation | undefined {
    return allLocations.find(f => f.path === currentPath);
}
